import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { PreferencesType } from "../../../models/preferences.model";
import { postImpexiumSubscriptionsUpdate } from "../../../redux/slice/impexium/impexium-subscriptions-update.slice";
import { retrieveImpexiumSubscriptions } from "../../../redux/slice/impexium/impexium-subscriptions.slice";
import { RootState, useAppDispatch } from "../../../redux/store";

import Header from "./news-letter-sub-header.component";
import Modal from "./news-letter-sub-modal.component";
import LoadingComp, { LoadingCompSmall } from "../../../components/re-usable/loading/loading";

interface Props {
  preferenceData: PreferencesType[];
  isUnsubscribeCalled: boolean;
  calledUnsubscribe: () => void;
}

interface SubscriptionFields {
  name: string;
  value: string;
}

interface SubnameLoadingChecker {
  name: string;
  loading: boolean;
}

const NewsLetterSubComponent = ({ preferenceData, isUnsubscribeCalled, calledUnsubscribe }: Props) => {
  const { data: userInfo } = useSelector((state: RootState) => state.impexiumUserInfo);
  const { data: subscriptions, loading: subscriptionsLoading } = useSelector((state: RootState) => state.impexiumSubscriptions);
  const { data: subscriptionsUpdate } = useSelector((state: RootState) => state.impexiumSubscriptionsUpdate);

  const [modalDescription, setModalDescription] = useState("");
  const [subName, setSubName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showUnsubModal, setShowUnsubModal] = useState(false);
  const [defaultSubscriptionList, setDefaultSubscriptionList] = useState<SubscriptionFields[]>([]);
  const [subscriptionList, setSubscriptionList] = useState<SubscriptionFields[]>([]);
  const [isSubscriptionMapping, setIsSubscriptionMapping] = useState(false);
  const [buttonLoading, setButtonLoading] = useState([] as SubnameLoadingChecker[]); // Records the subname that is loading
  const [isUnsubscribeAllCalled, setIsUnsubscribeAllCalled] = useState(false);

  const dispatch = useAppDispatch();

  const onClickSubscribe = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    setSubName(event.currentTarget.getAttribute("data-subname") as string);
    setModalDescription(event.currentTarget.getAttribute("data-bind") as string);
    setShowModal(true);
  };

  const onClickUnSubscribe = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    let subName = event.currentTarget.getAttribute("data-subname") as string;
    let value = subName.toLowerCase().includes("opt_out") ? "True" : "False";
    updateButtonLoading(subName, true);
    dispatch(postImpexiumSubscriptionsUpdate({ userId: userInfo.result.dataList[0].id, subscriptionField: [{ name: subName, value: value }] }));
  };

  const callUnsubcribe = () => {
    setShowUnsubModal(false);
    setIsUnsubscribeAllCalled(true);
    dispatch(postImpexiumSubscriptionsUpdate({ userId: userInfo.result.dataList[0].id, subscriptionField: defaultSubscriptionList }));
  };

  const handleModalEvent = () => {
    setShowModal(false);
  };

  const handleUnsubModalEvent = () => {
    setShowUnsubModal(false);
  };

  const handleShowUnsubModalEvent = () => {
    setShowUnsubModal(true);
  };

  const callUpdateApi = (subName: string) => {
    let value = subName.toLowerCase().includes("opt_out") ? "False" : "True";
    updateButtonLoading(subName, true);
    dispatch(postImpexiumSubscriptionsUpdate({ userId: userInfo.result.dataList[0].id, subscriptionField: [{ name: subName, value: value }] }));
  };

  const updateButtonLoading = (subName: string, loading: boolean) => {
    let buttonChecker = buttonLoading.find((x) => x.name === subName);
    if (buttonChecker !== undefined) {
      buttonChecker.loading = loading;
    } else {
      setButtonLoading((prev) => [...prev, { name: subName, loading: loading }]);
    }
  };

  useEffect(() => {
    if (isUnsubscribeCalled) {
      setIsUnsubscribeAllCalled(true);
    }
  }, [isUnsubscribeCalled])

  useEffect(() => {
    if (userInfo?.result?.dataList && preferenceData?.length > 0) {
      let newSubscriptionList = [] as SubscriptionFields[];

      preferenceData.map((item) => {
        newSubscriptionList.push({ name: item.subName, value: item.subName.toLowerCase().includes("opt_out") ? "True" : "False" });
      });

      setSubscriptionList(newSubscriptionList);
      setDefaultSubscriptionList(newSubscriptionList);

      let userId = userInfo.result.dataList[0].id;
      if (userId && !subscriptionsLoading) dispatch(retrieveImpexiumSubscriptions(userId));
    }
  }, [userInfo]);

  useEffect(() => {
    if (subscriptions?.result?.length > 0 && !isSubscriptionMapping) {
      setIsSubscriptionMapping(true);
      let newSubscriptionList = [] as SubscriptionFields[];
      subscriptions.result.map((item) => {
        let sub = subscriptionList.find((x) => x.name === item.name);

        if (sub !== undefined) {
          updateButtonLoading(sub.name, false);
          newSubscriptionList.push({ name: sub.name, value: item.value });
        }

        setSubscriptionList(newSubscriptionList);
      });
      setIsSubscriptionMapping(false);
      setIsUnsubscribeAllCalled(false);

      if (isUnsubscribeCalled) {
        calledUnsubscribe();
      }
    }
  }, [subscriptions]);

  useEffect(() => {
    if (subscriptionsUpdate?.result?.length > 0) {
      let userId = userInfo.result.dataList[0].id;
      if (userId && !subscriptionsLoading) dispatch(retrieveImpexiumSubscriptions(userId));
    }
  }, [subscriptionsUpdate]);

  if (subscriptions?.result?.length === undefined) return <LoadingComp />;

  return (
    <>
      <Header callUnsubcribe={handleShowUnsubModalEvent} />
      {isUnsubscribeAllCalled ? <LoadingComp /> : preferenceData.map((item, index) => {
        let sub = subscriptionList.find((x) => x.name === item.subName);
        let value = sub?.value || "False";
        let buttonChecker = buttonLoading.find((x) => x.name === item.subName);
        let isButtonLoading = buttonChecker === undefined ? false : buttonChecker.loading;

        return (
          <React.Fragment key={index}>
            {item.header !== "" && (
              <div className="col-md-12 media">
                <h2>
                  <b>{item.header}</b>
                </h2>
                <hr />
              </div>
            )}
            <div className="col-sm-12 col-md-6">
              <div className="media">
                <div className="media-body">
                  <h4 className="media-heading" dangerouslySetInnerHTML={{ __html: item.title }}></h4>
                  <p>{item.description}</p>
                  <div className="text-right">
                    {((value === "False" && !item.subName.toLowerCase().includes("opt_out")) || (value === "True" && item.subName.toLowerCase().includes("opt_out"))) &&
                      (isButtonLoading ? (
                        <LoadingCompSmall customStyle={{ verticalAlign: "middle", marginRight: "4em"  }} />
                      ) : (
                        <a className="btn btn-success subscription" type="button" style={{ marginRight: "2em" }} onClick={onClickSubscribe} data-bind={item.optInWording} data-subname={item.subName}>
                          {socialMediaList.includes(item.title) ? "Opt In" : "Subscribe"}
                        </a>
                      ))}
                    {((value === "False" && item.subName.toLowerCase().includes("opt_out")) || (value === "True" && !item.subName.toLowerCase().includes("opt_out"))) &&
                      (isButtonLoading ? (
                        <LoadingCompSmall customStyle={{ verticalAlign: "middle", marginRight: "4em" }} />
                      ) : (
                        <a className="btn subscription btn-danger" type="button" style={{ marginRight: "2em" }} onClick={onClickUnSubscribe} data-bind={item.optInWording} data-subname={item.subName}>
                        {socialMediaList.includes(item.title) ? "Opt Out" : "Unsubscribe"}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <style>
        {`
          #preferences-root .media {
            border: none;
          }
          .panel-body {
              padding: 15px 0;
          }
          @media only screen and (max-width: 960px) {
           #preferences-root .media .media-body {
             height: max-content;
           }
          }
        `}
      </style>
      {showModal && <Modal optInWording={modalDescription} setShowModal={handleModalEvent} callUpdateApi={callUpdateApi} subName={subName} showUnsubscribe={false} />}
      {showUnsubModal && <Modal setShowModal={handleUnsubModalEvent} callUnsubscribe={callUnsubcribe} showUnsubscribe={true} />}
    </>
  );
};

export default NewsLetterSubComponent;

const socialMediaList = ["Facebook", "Twitter", "LinkedIn", "Instagram", "TikTok", "Pinterest", "Reddit", "Snapchat", "Mobile"];
